import type { SortDirection } from '../../hooks/useTableSort';

interface SortableTableHeaderProps {
  /** Column key for sorting */
  columnKey: string;
  /** Display label */
  label: string;
  /** Whether this column is sortable */
  sortable?: boolean;
  /** Current sort direction for this column */
  sortDirection?: SortDirection;
  /** Callback when header is clicked */
  onSort?: (key: string) => void;
  /** Additional className */
  className?: string;
}

/**
 * Sortable table header cell component.
 *
 * @example
 * <SortableTableHeader
 *   columnKey="name"
 *   label="Name"
 *   sortable
 *   sortDirection={getSortDirection('name')}
 *   onSort={requestSort}
 * />
 */
export function SortableTableHeader({
  columnKey,
  label,
  sortable = true,
  sortDirection,
  onSort,
  className = '',
}: SortableTableHeaderProps) {
  const handleClick = () => {
    if (sortable && onSort) {
      onSort(columnKey);
    }
  };

  const baseClasses = 'px-6 py-3 text-left text-xs font-medium text-sage-600 uppercase tracking-wider';
  const sortableClasses = sortable
    ? 'cursor-pointer select-none hover:bg-sage-100 transition-colors group'
    : '';

  return (
    <th
      className={`${baseClasses} ${sortableClasses} ${className}`}
      onClick={handleClick}
      role={sortable ? 'button' : undefined}
      tabIndex={sortable ? 0 : undefined}
      onKeyDown={sortable ? (e) => e.key === 'Enter' && handleClick() : undefined}
      aria-sort={
        sortDirection === 'asc'
          ? 'ascending'
          : sortDirection === 'desc'
          ? 'descending'
          : undefined
      }
    >
      <div className="flex items-center gap-1.5">
        <span>{label}</span>
        {sortable && (
          <span className={`transition-opacity ${sortDirection ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'}`}>
            {sortDirection === 'asc' ? (
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            ) : sortDirection === 'desc' ? (
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            ) : (
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
              </svg>
            )}
          </span>
        )}
      </div>
    </th>
  );
}
